import { isPositiveNumber, reduce } from "et3-type-guards";

import { apiCallGetUsersData } from "../api/user-api/api-call-get-user-data";
import { SEARCH_BY_INPUT_USER_ID } from "../constants/picker-entries-constants";
import { E_TRANSFER_PACKAGE_TYPE, TGender, TTransferPackageType } from "../Types";

// isFound is kept separate from gender so a missing user reports "user not
// found" rather than being reported as a user whose gender we cannot read.
export type TUserGenderLookup = {
  isFound: boolean;
  gender: TGender;
};

export const isTransferPackageType = (
  value: string
): value is TTransferPackageType =>
  Object.values(E_TRANSFER_PACKAGE_TYPE).some(
    (packageType) => packageType === value
  );

const isGender = (value: string): value is TGender =>
  value === "m" || value === "f" || value === "";

export const isMaleOnlyPackage = (type: TTransferPackageType): boolean =>
  type === E_TRANSFER_PACKAGE_TYPE.PLUS ||
  type === E_TRANSFER_PACKAGE_TYPE.SILVER ||
  type === E_TRANSFER_PACKAGE_TYPE.GOLD ||
  type === E_TRANSFER_PACKAGE_TYPE.PLATINUM ||
  type === E_TRANSFER_PACKAGE_TYPE.BLACK;

export const isGenderedPackage = (type: TTransferPackageType): boolean =>
  type === E_TRANSFER_PACKAGE_TYPE.VIP || isMaleOnlyPackage(type);

// Calls the stateless api layer directly. Routing this through
// getUserAdvancedStore would call resetData() and wipe whatever the admin has
// open on the Advanced page, and two concurrent lookups would clobber each other.
export const fetchUserGender = async (
  userId: number
): Promise<TUserGenderLookup> => {
  const { status, data } = await apiCallGetUsersData({
    searchType: "option_search",
    condition: SEARCH_BY_INPUT_USER_ID,
    value: `${userId}`,
    userName: " ",
    userNationality: " ",
    userResidenceCountry: " ",
    userAge: 0,
    userHeight: 0,
    userWeight: 0,
    userDescription: " ",
    userPeerDescription: " ",
    userAccountCreatedOn: " "
  });

  // The error branch returns USER_DEFAULT_DATA, whose gender is "" — reading it
  // without this guard would pass an unknown gender off as a real one.
  if (status !== "OK") {
    return { isFound: false, gender: "" };
  }

  // USER_DEFAULT_DATA also carries no real userId, so this separates a genuine
  // record from the placeholder the api hands back when the lookup misses.
  const foundUserId = reduce(() => data[0].userId, 0);
  if (!isPositiveNumber(foundUserId)) {
    return { isFound: false, gender: "" };
  }

  const gender = reduce(() => data[0].gender, "");
  return { isFound: true, gender: isGender(gender) ? gender : "" };
};
